import React, { useEffect, useState } from 'react';

import ApiClient from '@lib/apiClient';

import { convertNumberToUSFormat, parseQuoteName } from '@utils/Util';

const BREAKDOWN_KEY = 'price_quantity_breakdown';

const formatDateTime = (value) => {
	if (!value) return '';
	const date = new Date(value);
	if (isNaN(date.getTime())) return value;
	return date.toLocaleString('en-US', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit'
	});
};

const getBreakdownSnapshot = (log, field) => {
	const change = log?.changes?.[BREAKDOWN_KEY];
	const snapshot = change ? change[field] : null;
	if (!snapshot || !Array.isArray(snapshot.lines) || snapshot.lines.length === 0) return null;
	return snapshot;
};

const SnapshotTable = ({ snapshot }) => {
	if (!snapshot) return null;

	const lines = snapshot.lines || [];
	const hasPoCode = lines.some((line) => line.poItemIdentifierCode);
	const totalQuantity = lines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
	const totalAmount = lines.reduce(
		(sum, line) => sum + (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0),
		0
	);

	return (
		<div className="rfq-history-snapshot">
			{snapshot.quotationType && (
				<p className="regular-12 gray_dark_02 mb-2">Price {parseQuoteName(snapshot.quotationType)}</p>
			)}
			<table className="rfq-history-table">
				<thead>
					<tr>
						<th>Color</th>
						<th>Size</th>
						{hasPoCode && <th>PO item code</th>}
						<th className="text-right">Quantity</th>
						<th className="text-right">Unit price ($)</th>
					</tr>
				</thead>
				<tbody>
					{lines.map((line, index) => (
						<tr key={`${line.colorId}-${line.sizeId}-${index}`}>
							<td>{line.color || '-'}</td>
							<td>{line.size || '-'}</td>
							{hasPoCode && <td>{line.poItemIdentifierCode || '-'}</td>}
							<td className="text-right">
								{convertNumberToUSFormat(line.quantity, true) || 0}
							</td>
							<td className="text-right">
								{convertNumberToUSFormat(line.unitPrice, false, true) || '$0'}
							</td>
						</tr>
					))}
				</tbody>
				<tfoot>
					<tr>
						<td colSpan={hasPoCode ? 3 : 2} className="semibold-12">
							Total
						</td>
						<td className="text-right semibold-12">
							{convertNumberToUSFormat(totalQuantity, true)} QTY
						</td>
						<td className="text-right semibold-12">
							{convertNumberToUSFormat(totalAmount, false, true)}
						</td>
					</tr>
				</tfoot>
			</table>
		</div>
	);
};

const RfqChangeHistory = ({ rfqId }) => {
	const [logs, setLogs] = useState([]);
	const [isOpen, setIsOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);

	useEffect(() => {
		if (!rfqId) return;

		let isMounted = true;
		ApiClient.GET('getRfqChangeLog', `${rfqId}/change-log`)
			.then((response) => {
				if (!isMounted) return;
				const data = Array.isArray(response?.data) ? response.data : [];
				// Keep only entries that carry a price/quantity breakdown to show as a table.
				const breakdownLogs = data.filter((log) => getBreakdownSnapshot(log, 'oldValue'));
				setLogs(breakdownLogs);
			})
			.catch(() => {
				if (isMounted) setLogs([]);
			});

		return () => {
			isMounted = false;
		};
	}, [rfqId]);

	if (!logs.length) return null;

	return (
		<div className="rfq-history-wrapper">
			<span
				className="rfq-history-badge"
				onClick={() => setIsOpen((prev) => !prev)}
				role="button"
				tabIndex={0}
			>
				Edited
				<img
					src={`${process.env.PUBLIC_URL}/icons/down-arrow-gray-dark-02.svg`}
					alt="toggle"
					className={`rfq-history-caret ${isOpen ? 'open' : ''}`}
				/>
			</span>

			{isOpen && (
				<div className="rfq-history-panel">
					<p className="regular-12 gray_dark_02 mb-2">Previous versions</p>
					{logs.map((log, index) => {
						const isActive = index === activeIndex;
						return (
							<div className="rfq-history-item" key={log.id}>
								<div
									className={`rfq-history-item-header ${isActive ? 'active' : ''}`}
									onClick={() => setActiveIndex(isActive ? -1 : index)}
									role="button"
									tabIndex={0}
								>
									<span className="semibold-12">{formatDateTime(log.changedAt)}</span>
									<img
										src={`${process.env.PUBLIC_URL}/icons/down-arrow-gray-dark-02.svg`}
										alt="toggle"
										className={`rfq-history-caret ${isActive ? 'open' : ''}`}
									/>
								</div>
								{isActive && (
									<div className="rfq-history-item-body">
										<SnapshotTable snapshot={getBreakdownSnapshot(log, 'oldValue')} />
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default RfqChangeHistory;
